'use client'

import { markVotedVideo, trackVideoFeedback } from '@/lib/videoFeedback'
import { FC, useEffect, useState } from 'react'

const THANK_YOU_DELAY_MS = 2000

export interface VideoFeedbackScreenProps {
  videoId: string
  scenarioId?: string
  variant: 'pc' | 'sp'
  onDone: () => void
  /** 回答せずに閉じる（未指定ならスキップ導線を出さない） */
  onSkip?: () => void
  trackingEnabled?: boolean
}

const VideoFeedbackScreen: FC<VideoFeedbackScreenProps> = ({
  videoId,
  scenarioId,
  variant,
  onDone,
  onSkip,
  trackingEnabled = true,
}) => {
  const [voted, setVoted] = useState(false)

  const handleVote = (isHelpful: boolean) => {
    if (voted) return
    setVoted(true)
    if (trackingEnabled) {
      markVotedVideo(videoId)
      trackVideoFeedback(videoId, isHelpful, 'EMBED', scenarioId)
    }
  }

  useEffect(() => {
    if (!voted) return
    const timer = setTimeout(onDone, THANK_YOU_DELAY_MS)
    return () => clearTimeout(timer)
  }, [voted, onDone])

  const containerClassName =
    variant === 'sp' ? 'feedback-screen feedback-screen-sp' : 'feedback-screen feedback-screen-pc'

  return (
    <>
      <style>{`
        .feedback-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 24px;
          padding: 24px;
          text-align: center;
        }

        .feedback-screen-pc {
          flex: 1;
        }

        /*
         * SP埋め込みは動画エリア（.sp-video-container）だけを覆う。全画面 fixed にすると
         * 「別の動画へ／CTA」もモーダルの閉じるボタンも隠れ、回答以外の出口が無くなる。
         * z-index は .sp-right-controls（20）より下に置き、閉じる・速度・ミュートは押せるまま残す。
         */
        .feedback-screen-sp {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.92);
          z-index: 18;
          animation: feedbackFadeIn 300ms ease-out;
        }

        .feedback-skip {
          padding: 4px 8px;
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          font-size: 13px;
          cursor: pointer;
        }

        .feedback-skip:hover {
          color: white;
        }

        @keyframes feedbackFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .feedback-question {
          color: white;
          font-size: 18px;
          font-weight: bold;
          line-height: 1.5;
        }

        .feedback-buttons {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 16px;
          width: 100%;
          max-width: 376px;
        }

        .feedback-button {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1 1 0;
          gap: 8px;
          padding: 14px 16px;
          min-width: 0;
          white-space: nowrap;
          font-weight: bold;
          color: white;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 8px;
          cursor: pointer;
          transition: all 200ms ease;
        }

        .feedback-screen-pc .feedback-button {
          font-size: 14.2px;
        }

        .feedback-screen-sp .feedback-button {
          font-size: 13px;
        }

        .feedback-button:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .feedback-thanks {
          color: white;
          font-size: 18px;
          font-weight: bold;
        }
      `}</style>

      <div className={containerClassName}>
        {voted ? (
          <p className="feedback-thanks">ご回答ありがとうございました！</p>
        ) : (
          <>
            <p className="feedback-question">この動画は参考になりましたか？</p>
            <div className="feedback-buttons">
              <button type="button" className="feedback-button" onClick={() => handleVote(true)}>
                参考になった
              </button>
              <button type="button" className="feedback-button" onClick={() => handleVote(false)}>
                参考にならなかった
              </button>
            </div>
            {onSkip && (
              <button type="button" className="feedback-skip" onClick={onSkip}>
                スキップ
              </button>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default VideoFeedbackScreen
